import { formatEventDate } from './tools'

/**
 * Represents an Event object. Used as a wrapper around an Event fetched
 * from WordPress to provide more functionality.
 */
export default class Event {
    constructor(event) {
        if (!event) {
            this.event = null
            return
        }
        this.event = JSON.parse(JSON.stringify(event))
        this.setDefaults()
    }

    /**
     * Set defaults and adds properties to the event object.
     */
    setDefaults() {
        this.addDefaultProperty('isCurrentPage', this.isCurrentPage())
        this.addDefaultProperty('isPrivate', this.isPrivate())
        this.addDefaultProperty(
            'date',
            new Date(this.event.startDate.replace(/\s/, 'T')),
        )
        this.addDefaultProperty('inPast', this.inPast())
        this.addDefaultProperty('eventDate', this.formatEventDate())
    }

    /**
     * Adds a value to a property if that property does not exist.
     *
     * @param {string} property The property of the post object.
     * @param {any} value  The default property value.
     */
    addDefaultProperty(property, value) {
        this.event[property] = this.event?.[property] ?? value
    }

    /**
     * Make sure the link and post properties are set.
     * As of now, there is no difference between the two.
     */
    confirmLinkAndURI() {
        // We have a link, but no URI
        if (this.event?.link && !this.event?.uri) {
            this.event.uri = this.event.link
        }

        // We have an URI, but no link
        if (this.event?.uri && !this.event?.link) {
            this.event.link = this.event.uri
        }

        // We don't have a link or URI
        if (!this.event?.link && !this.event?.uri) {
            this.event.link = this.event.uri = ''
        }
    }

    /**
     * If slug is not set, retrieve it from the post URI.
     */
    confirmSlug() {
        if (!this.event?.slug) {
            var uri = this.stripTrailingSlash(this.event.uri)
            this.event.slug = uri.substring(uri.lastIndexOf('/') + 1)
        }
    }

    /**
     * Simply returns the event object.
     * @returns Event object
     */
    getEvent() {
        return this.event
    }

    /**
     * Checks to see if the page is the current page in the browser.
     * @returns Boolean
     */
    isCurrentPage() {
        return (
            this.event.link ===
            (typeof window !== 'undefined' && window.location.pathname)
        )
    }

    isPrivate() {
        return this.event?.privatePost?.isprivate ?? false
    }

    /**
     * Removes '/' from the end of a string.
     *
     * @param {string} url
     * @returns string without a trailins slash
     */
    stripTrailingSlash(url) {
        return url.endsWith('/') ? url.slice(0, -1) : url
    }

    /**
     * Checks to see if date is in the past.
     *
     * @param {Date} date
     * @returns boolean
     */
    inPast() {
        if (!this.event.date instanceof Date) {
            return false
        }
        return this.event.date.getTime() < new Date().getTime()
    }

    /**
     * Formats the event date.
     *
     * @returns string
     */
    formatEventDate() {
        if (!this.event.startDate) {
            return ''
        }

        return formatEventDate(this.event.startDate, this.event?.endDate)
    }
}

/**
 * title: string
 * startDate: string '2023-03-05 18:00:00'
 * endDate: string
 * excerpt: string
 * featured: boolean
 * featuredImage: {node: { localFile: { childImageSharp: {gatsbyImageData: } , publicURL: string } } }
 * sortDate: string
 * products: object {nodes: [{name: 'Chapters}]}
 * link: string
 * url: null
 * venue: object { title: string, city: string, state: string }
 * privatePost: { isPrivate: boolean | null }
 */
